import React, { useState, useEffect } from "react";
import { Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { toMoney } from "../../../../helpers";
import { extendHmoExpiry } from "../../../../redux/actions";
import ModalExtendExpiry from "../../ManageHmo/ModalExtendExpiry";
import Container from "./style";

const BasicInfo = ({ activeUser, onUserUpdated, onEditUser }) => {
  const dispatch = useDispatch();
  const { gymLocations } = useSelector((s) => s.dashStats);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendLoading, setExtendLoading] = useState(false);
  const [expiryDate, setExpiryDate] = useState(activeUser?.hmo_expires_at);

  useEffect(() => {
    setExpiryDate(activeUser?.hmo_expires_at);
  }, [activeUser?.hmo_expires_at]);

  const handleExtendSubmit = async (values) => {
    setExtendLoading(true);
    const res = await dispatch(extendHmoExpiry(activeUser.id, values));
    setExtendLoading(false);
    if (res && res.success) {
      setExpiryDate(values.hmo_expires_at);
      setShowExtendModal(false);
      if (onUserUpdated) {
        onUserUpdated({
          hmo_expires_at: values.hmo_expires_at,
          hmo_status: "approved",
        });
      }
    }
  };

  return (
    <Container>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <h1 style={{ margin: 0 }}>Basic Info</h1>
        {onEditUser && (
          <Button
            type="primary"
            size="small"
            onClick={onEditUser}
            style={{ fontSize: "12px", height: "28px" }}
          >
            ✏️ Edit All Details
          </Button>
        )}
      </div>
      <hr />
      <div className="info-container">
        <div>
          <p>Name</p>
          <p>{activeUser.name}</p>
        </div>
        <div>
          <p>Email</p>
          <p>{activeUser.email}</p>
        </div>
        <div>
          <p>Phone</p>
          <p>{activeUser.phone || "Not Provided"}</p>
        </div>
        <div>
          <p>Wallet Balance</p>
          <p>{toMoney(activeUser.wallet_balance)}</p>
        </div>
        <div>
          <p>Gender</p>
          <p>
            {activeUser.gender && activeUser.gender.toLowerCase() === "m"
              ? "Male"
              : activeUser.gender && activeUser.gender.toLowerCase() === "f"
              ? "Female"
              : "Not Specified"}
          </p>
        </div>
        <div>
          <p>Date of Birth</p>
          <p>{activeUser.dob ? String(activeUser.dob).substring(0, 10) : "Not Provided"}</p>
        </div>
        <div>
          <p>Gym Location</p>
          <p>
            {activeUser.location?.name ||
              activeUser.location_name ||
              gymLocations?.[activeUser.location_id] ||
              "Main Branch"}
          </p>
        </div>
        <div>
          <p>Account Status</p>
          <p style={{ textTransform: "capitalize", fontWeight: 600 }}>
            <span
              style={{
                color:
                  activeUser.status === "active"
                    ? "#27ae60"
                    : activeUser.status === "banned"
                    ? "#e74c3c"
                    : "#e67e22",
              }}
            >
              {activeUser.status || "active"}
            </span>
          </p>
        </div>
        <div>
          <p>Total Checkins</p>
          <p>{activeUser.total_check_ins}</p>
        </div>
        <div>
          <p>Total Paid</p>
          <p>{toMoney(activeUser?.total_paid)}</p>
        </div>
      </div>

      <h1 style={{ marginTop: "16px", color: "#27ae60" }}>
        ⚖️ Physical Metrics
      </h1>
      <hr />
      <div className="info-container">
        <div>
          <p>Height</p>
          <p>
            {activeUser.height && parseFloat(activeUser.height) > 0
              ? `${parseFloat(activeUser.height)} cm`
              : "Not Provided"}
          </p>
        </div>
        <div>
          <p>Weight</p>
          <p>
            {activeUser.weight && parseFloat(activeUser.weight) > 0
              ? `${parseFloat(activeUser.weight)} kg`
              : "Not Provided"}
          </p>
        </div>
      </div>

      <h1 style={{ marginTop: "16px", color: "#e74c3c" }}>
        🚨 Emergency Contact
      </h1>
      <hr />
      <div className="info-container">
        <div>
          <p>Contact Name</p>
          <p>{activeUser.emergency_contact_name || "Not Provided"}</p>
        </div>
        <div>
          <p>Contact Phone</p>
          <p>{activeUser.emergency_contact_phone || "Not Provided"}</p>
        </div>
        <div>
          <p>Relationship</p>
          <p>{activeUser.emergency_contact_relationship || "Not Provided"}</p>
        </div>
      </div>

      <h1 style={{ marginTop: "16px", color: "#108ee9" }}>
        🏥 HMO Information
      </h1>
      <hr />
      <div className="info-container">
        <div>
          <p>HMO Provider</p>
          <p>{activeUser.hmo_name || activeUser.hmo?.name || "None / Not Registered"}</p>
        </div>
        <div>
          <p>HMO Number</p>
          <p>{activeUser.hmo_number || "None"}</p>
        </div>
        <div>
          <p>HMO Status</p>
          <p>
            {activeUser.hmo_status === "approved" ? (
              <span style={{ color: "#27ae60", fontWeight: 600 }}>Approved</span>
            ) : activeUser.hmo_status === "pending" ? (
              <span style={{ color: "#e67e22", fontWeight: 600 }}>Pending Approval</span>
            ) : activeUser.hmo_status === "rejected" ? (
              <span style={{ color: "#e74c3c", fontWeight: 600 }}>Rejected</span>
            ) : (
              "None"
            )}
          </p>
        </div>
        <div>
          <p>Expiration Date</p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
            <p style={{ margin: 0 }}>{expiryDate ? String(expiryDate).substring(0, 10) : "N/A"}</p>
            {(activeUser.hmo_id || activeUser.hmo) && (
              <Button
                type="primary"
                size="small"
                onClick={() => setShowExtendModal(true)}
                style={{ fontSize: "11px", height: "24px", padding: "0 8px" }}
              >
                Extend Expiry
              </Button>
            )}
          </div>
        </div>
        {activeUser.hmo && (
          <>
            <div>
              <p>Weekly Allowance</p>
              <p>{activeUser.hmo.allowable_checks_per_week || 3} visits / week</p>
            </div>
            <div>
              <p>Visits This Week</p>
              <p>{activeUser.hmo_checks_this_week || 0} visits used</p>
            </div>
          </>
        )}
      </div>

      <ModalExtendExpiry
        visible={showExtendModal}
        user={activeUser}
        loading={extendLoading}
        onClose={() => setShowExtendModal(false)}
        onSubmit={handleExtendSubmit}
      />
    </Container>
  );
};

export default BasicInfo;
